// The arithmetic of the charts of the Analytics area: scales, bars and the segments of a ring. Pure, so a test proves it. The charts are SVG with
// 1 px grid lines and palette colours, and every number a chart shows is also written as text next to it.

/** A tick step of 1, 2, 5 or 10 times a power of ten: the axis ends on a round number. */
export function niceMax(value: number): number {
  if (value <= 0) return 4;
  const exponent = 10 ** Math.floor(Math.log10(value));
  const fraction = value / exponent;
  const step = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return Math.max(4, step * exponent);
}

/** The grid lines: 0, the middle and the top of the scale. Three lines are enough to read a bar and keep the card quiet. */
export function gridValues(max: number): readonly [number, number, number] {
  return [0, max / 2, max];
}

export type Bar = Readonly<{ x: number; width: number; height: number; y: number }>;

/** Bars in a box of `width` x `height`, one slot per value, a zero stays a 1 px stub so a quiet bucket is a bucket and not a hole. */
export function barLayout(
  values: readonly number[],
  box: Readonly<{ width: number; height: number }>,
  max: number,
  gapRatio = 0.28,
): readonly Bar[] {
  if (values.length === 0) return [];
  const slot = box.width / values.length;
  const width = Math.max(1, slot * (1 - gapRatio));
  return values.map((value, index) => {
    const height = value <= 0 || max <= 0 ? 1 : Math.max(2, (value / max) * box.height);
    return { x: index * slot + (slot - width) / 2, width, height, y: box.height - height };
  });
}

export type RingSegment = Readonly<{ dash: number; gap: number; offset: number }>;

/**
 * The segments of a ring drawn with `stroke-dasharray` on circles of radius `radius`: each segment is a dash of its share of the circumference,
 * placed by `stroke-dashoffset`. A thin gap separates two segments, so neighbours with the same colour family stay apart.
 */
export function ringSegments(
  counts: readonly number[],
  radius: number,
  gap = 2,
): readonly RingSegment[] {
  const total = counts.reduce((sum, count) => sum + count, 0);
  const circumference = 2 * Math.PI * radius;
  if (total === 0) return [];
  let used = 0;
  return counts.map((count) => {
    const length = (count / total) * circumference;
    const dash = Math.max(0, length - (counts.length > 1 ? gap : 0));
    const segment = { dash, gap: circumference - dash, offset: used === 0 ? 0 : -used };
    used += length;
    return segment;
  });
}
